import React, { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  UserCog,
  Loader2,
  Mail,
  RefreshCw,
} from "lucide-react";

const DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

/**
 * Safely extract a human-readable string from an API error.
 * Handles:
 *   - Plain string:  "Something went wrong"
 *   - Pydantic v2 array: [{ loc, msg, type, input, ctx }, ...]
 *   - FastAPI string detail: { detail: "..." }
 */
const getErrorMessage = (error, fallback = "An unexpected error occurred") => {
  const detail = error?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        const field = Array.isArray(d.loc) ? d.loc.join(" → ") : "";
        return field ? `${field}: ${d.msg}` : d.msg;
      })
      .join("; ");
  }
  return fallback;
};

// Short labels for the day-of-week chips on the staff card
const DAY_SHORT = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

const StaffPage = () => {
  const { api, business } = useAuth();
  const [staff, setStaff] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    email: "",
    name: "",
    title: "",
    avatar_url: "",
    bio: "",
    schedules: DAYS.map((_, i) => ({
      day: i,
      start_time: "09:00",
      end_time: "17:00",
      is_available: i < 5,
    })),
  });

  useEffect(() => {
    if (business?.id) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business]);

  const fetchData = async () => {
    try {
      const [staffRes, servicesRes] = await Promise.all([
        api.get(`/businesses/${business.id}/staff`),
        api.get(`/businesses/${business.id}/services`),
      ]);
      setStaff(staffRes.data);
      setServices(servicesRes.data.filter((s) => s.is_active !== false));
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load staff");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      email: "",
      name: "",
      title: "",
      avatar_url: "",
      bio: "",
      schedules: DAYS.map((_, i) => ({
        day: i,
        start_time: "09:00",
        end_time: "17:00",
        is_available: i < 5,
      })),
    });
    setEditingStaff(null);
  };

  const openEditDialog = (staffMember) => {
    setEditingStaff(staffMember);
    setFormData({
      email: staffMember.email || "",
      name: staffMember.name || "",
      title: staffMember.title || "",
      avatar_url: staffMember.avatar_url || "",
      bio: staffMember.bio || "",
      schedules:
        staffMember.schedules?.length > 0
          ? DAYS.map((_, i) => {
              const existing = staffMember.schedules.find((s) => s.day === i);
              return (
                existing || {
                  day: i,
                  start_time: "09:00",
                  end_time: "17:00",
                  is_available: false,
                }
              );
            })
          : DAYS.map((_, i) => ({
              day: i,
              start_time: "09:00",
              end_time: "17:00",
              is_available: i < 5,
            })),
    });
    setDialogOpen(true);
  };

  const updateSchedule = (dayIndex, field, value) => {
    setFormData((prev) => ({
      ...prev,
      schedules: prev.schedules.map((s, i) =>
        i === dayIndex ? { ...s, [field]: value } : s,
      ),
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error("Staff name is required");
      return;
    }

    // Email is optional — but if provided it must look like a valid address
    if (
      (formData.email || "").trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((formData.email || "").trim())
    ) {
      toast.error("Please enter a valid email address, or leave it blank");
      return;
    }

    setSaving(true);
    try {
      if (editingStaff) {
        await api.put(`/businesses/${business.id}/staff/${editingStaff.id}`, {
          name: formData.name,
          title: formData.title,
          avatar_url: formData.avatar_url,
          bio: formData.bio,
          schedules: formData.schedules,
        });
        toast.success("Staff updated");
      } else {
        // Build invite payload — omit email entirely when blank so the
        // backend Optional[EmailStr] field receives null rather than ""
        const invitePayload = {
          name: formData.name,
          title: formData.title,
          avatar_url: formData.avatar_url,
          bio: formData.bio,
          schedules: formData.schedules,
          ...((formData.email || "").trim()
            ? { email: (formData.email || "").trim() }
            : {}),
        };
        await api.post(
          `/businesses/${business.id}/staff/invite`,
          invitePayload,
        );
        toast.success("Staff member added");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Failed to save staff:", error);
      toast.error(getErrorMessage(error, "Failed to save staff"));
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (staffId) => {
    if (!window.confirm("Are you sure you want to remove this staff member?"))
      return;

    try {
      await api.delete(`/businesses/${business.id}/staff/${staffId}`);
      toast.success("Staff removed");
      fetchData();
    } catch (error) {
      console.error("Failed to remove staff:", error);
      toast.error(getErrorMessage(error, "Failed to remove staff"));
    }
  };

  const handleResendInvite = async (staffId, staffEmail, staffName) => {
    if (!staffEmail) {
      toast.error("This staff member has no email address");
      return;
    }

    try {
      await api.post(
        `/businesses/${business.id}/staff/${staffId}/resend-invite`,
      );
      toast.success(`Invitation resent to ${staffEmail}`);
    } catch (error) {
      console.error("Failed to resend invite:", error);
      toast.error(getErrorMessage(error, "Failed to resend invitation"));
    }
  };

  // Check if staff needs invitation (has email but no password set)
  const needsInvite = (member) => {
    return member.email && !member.password_hash && !member.has_password;
  };

  // ── Helper: get services assigned to a staff member ──────────────────────
  // ServicesPage is the single source of truth for service assignments.
  // Each service document in db.services has an assigned_staff_ids[] array.
  const getAssignedServices = (member) => {
    return services.filter((svc) =>
      svc.assigned_staff_ids?.includes(member.id),
    );
  };

  const getAssignedServiceCount = (member) =>
    getAssignedServices(member).length;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-muted-foreground">
            Loading staff...
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="staff-page">
        {/* ── Header ── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-extrabold">Staff</h1>
            <p className="text-muted-foreground">
              Manage your team members and their schedules.
            </p>
          </div>

          {/* ── Add / Edit dialog ── */}
          <Dialog
            open={dialogOpen}
            onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) resetForm();
            }}
          >
            <DialogTrigger asChild>
              <Button data-testid="invite-staff-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Staff
              </Button>
            </DialogTrigger>

            {/*
              Mobile: full-screen with pinned header + scrollable body.
              Desktop: wide modal capped at 90 vh.
            */}
            <DialogContent
              className="
                flex flex-col gap-0 p-0
                w-full max-w-2xl
                h-[100dvh] sm:h-auto sm:max-h-[90vh]
                rounded-none sm:rounded-lg
              "
            >
              {/* Fixed header */}
              <DialogHeader className="px-6 pt-6 pb-4 border-b border-border shrink-0">
                <DialogTitle className="text-xl">
                  {editingStaff ? "Edit Staff Member" : "Add Staff Member"}
                </DialogTitle>
              </DialogHeader>

              {/* Scrollable form body */}
              <div className="overflow-y-auto flex-1 px-6 py-5">
                <div className="space-y-6">
                  {/* Name + Email */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="name" className="text-base font-semibold">
                        Name <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            name: e.target.value,
                          }))
                        }
                        placeholder="John Doe"
                        className="h-12 text-base"
                        data-testid="staff-name-input"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label
                        htmlFor="email"
                        className="text-base font-semibold"
                      >
                        Email{" "}
                        <span className="text-muted-foreground font-normal">
                          (Optional)
                        </span>
                      </Label>
                      <Input
                        id="email"
                        type="text"
                        inputMode="email"
                        value={formData.email}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            email: e.target.value,
                          }))
                        }
                        placeholder="Enter email (optional)"
                        disabled={!!editingStaff}
                        className="h-12 text-base"
                        data-testid="staff-email-input"
                      />
                    </div>
                  </div>

                  {/* Title + Avatar URL (displayed on public booking Team tab) */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label
                        htmlFor="staff-title"
                        className="text-base font-semibold"
                      >
                        Title{" "}
                        <span className="text-muted-foreground font-normal">
                          (Optional)
                        </span>
                      </Label>
                      <Input
                        id="staff-title"
                        value={formData.title}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            title: e.target.value,
                          }))
                        }
                        placeholder="e.g. Beauty Therapist"
                        className="h-12 text-base"
                        data-testid="staff-title-input"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label
                        htmlFor="staff-avatar-url"
                        className="text-base font-semibold"
                      >
                        Photo URL{" "}
                        <span className="text-muted-foreground font-normal">
                          (Optional)
                        </span>
                      </Label>
                      <Input
                        id="staff-avatar-url"
                        type="url"
                        value={formData.avatar_url}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            avatar_url: e.target.value,
                          }))
                        }
                        placeholder="https://…"
                        className="h-12 text-base"
                        data-testid="staff-avatar-url-input"
                      />
                    </div>
                  </div>

                  {/* Bio (Optional) — used on public booking Team & Staff Selection */}
                  <div className="space-y-1.5">
                    <Label
                      htmlFor="staff-bio"
                      className="text-base font-semibold"
                    >
                      Bio{" "}
                      <span className="text-muted-foreground font-normal">
                        (Optional)
                      </span>
                    </Label>
                    <Textarea
                      id="staff-bio"
                      value={formData.bio}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          bio: e.target.value,
                        }))
                      }
                      placeholder="Skills, experience, specialities, certifications…"
                      rows={4}
                      className="text-base resize-y"
                      data-testid="staff-bio-input"
                    />
                  </div>

                  {/* Working Hours */}
                  <div className="space-y-3">
                    <Label className="text-base font-semibold">
                      Working Hours
                    </Label>
                    <div className="space-y-2">
                      {DAYS.map((day, i) => (
                        <div
                          key={day}
                          className="rounded-lg border border-border p-3 space-y-2"
                        >
                          {/* Row 1: day name + available toggle */}
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium w-24">
                              {day}
                            </span>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.schedules[i].is_available}
                                onChange={(e) =>
                                  updateSchedule(
                                    i,
                                    "is_available",
                                    e.target.checked,
                                  )
                                }
                                className="w-4 h-4 rounded accent-primary"
                                data-testid={`staff-day-${i}-toggle`}
                              />
                              <span className="text-sm text-muted-foreground">
                                Available
                              </span>
                            </label>
                          </div>

                          {/* Row 2: time pickers — only shown when available */}
                          {formData.schedules[i].is_available && (
                            <div className="flex items-center gap-2 pt-1">
                              <Input
                                type="time"
                                value={formData.schedules[i].start_time}
                                onChange={(e) =>
                                  updateSchedule(
                                    i,
                                    "start_time",
                                    e.target.value,
                                  )
                                }
                                className="flex-1 h-10 text-sm"
                              />
                              <span className="text-muted-foreground text-sm shrink-0">
                                to
                              </span>
                              <Input
                                type="time"
                                value={formData.schedules[i].end_time}
                                onChange={(e) =>
                                  updateSchedule(i, "end_time", e.target.value)
                                }
                                className="flex-1 h-10 text-sm"
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Read-only assigned services display */}
                  <div className="space-y-2">
                    <Label className="text-base font-semibold">
                      Assigned Services
                    </Label>
                    {editingStaff ? (
                      (() => {
                        const assignedServices =
                          getAssignedServices(editingStaff);
                        return assignedServices.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {assignedServices.map((svc) => (
                              <span
                                key={svc.id}
                                className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-accent/10 text-accent border border-accent/20"
                              >
                                {svc.name}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground italic">
                            No services assigned yet.
                          </p>
                        );
                      })()
                    ) : (
                      <p className="text-sm text-muted-foreground italic">
                        Services can be assigned after the staff member is
                        added.
                      </p>
                    )}
                    <div className="rounded-md bg-muted px-4 py-2.5 text-sm text-muted-foreground">
                      Service assignments are managed from the{" "}
                      <span className="font-semibold text-foreground">
                        Services
                      </span>{" "}
                      page.
                    </div>
                  </div>

                  {/* Submit */}
                  <Button
                    type="button"
                    onClick={handleSubmit}
                    className="w-full h-12 text-base font-semibold"
                    disabled={saving}
                    data-testid="save-staff-btn"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        {editingStaff ? "Updating..." : "Saving..."}
                      </>
                    ) : editingStaff ? (
                      "Update Staff Member"
                    ) : (
                      "Save Staff Member"
                    )}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* ── Staff list ── */}
        {staff.length === 0 ? (
          <div className="card-base text-center py-12">
            <UserCog className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="font-heading font-bold text-lg mb-2">
              No staff members yet
            </h3>
            <p className="text-muted-foreground mb-4">
              Add your first team member to get started.
            </p>
            <Button onClick={() => setDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Staff
            </Button>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {staff.map((member) => (
              <div key={member.id} className="card-base">
                {/* Avatar + name + action buttons */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center shrink-0">
                      <span className="font-heading font-bold text-lg">
                        {(
                          member.name?.[0] ||
                          member.email?.[0] ||
                          "?"
                        ).toUpperCase()}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-heading font-bold truncate">
                        {member.name || "Unnamed"}
                      </h3>
                      <p className="text-sm text-muted-foreground truncate">
                        {member.email || "No email"}
                        {member.email && !member.password_hash && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                            Invite Pending
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0 ml-2">
                    {/* Resend Invite button - only show if email exists but no password */}
                    {member.email && !member.password_hash && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          handleResendInvite(
                            member.id,
                            member.email,
                            member.name,
                          )
                        }
                        className="text-violet-600 hover:text-violet-700 hover:bg-violet-50"
                        title="Resend invitation email"
                        data-testid={`resend-invite-${member.id}`}
                      >
                        <Mail className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditDialog(member)}
                      data-testid={`edit-staff-${member.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemove(member.id)}
                      className="text-destructive hover:text-destructive"
                      data-testid={`remove-staff-${member.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Working days chips */}
                <div className="mb-3">
                  <span className="text-xs text-muted-foreground">
                    Working Days
                  </span>
                  <div className="flex gap-1 mt-1 flex-wrap">
                    {DAYS.map((day, i) => {
                      const schedule = member.schedules?.find(
                        (s) => s.day === i,
                      );
                      const isAvailable = schedule?.is_available;
                      return (
                        <div
                          key={day}
                          className={`w-8 h-8 rounded flex items-center justify-center text-xs font-medium ${
                            isAvailable
                              ? "bg-accent/10 text-accent"
                              : "bg-muted text-muted-foreground"
                          }`}
                          title={`${day}: ${isAvailable ? "Available" : "Off"}`}
                        >
                          {DAY_SHORT[i]}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Assigned services count */}
                <div className="pt-3 border-t border-border">
                  {(() => {
                    const count = getAssignedServiceCount(member);
                    return (
                      <span className="text-xs text-muted-foreground">
                        {count} service{count !== 1 ? "s" : ""} assigned
                      </span>
                    );
                  })()}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default StaffPage;
